import React, { useEffect } from "react";
import { Button, Col, message, Modal, Row } from "antd";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import PMSSelect from "../../../components/Pms/PmsSelect/PMSSelect";
import {
  useGetPMS,
  useAddPMSReportList,
  useUpdatePMSReport,
} from "../../../hooks/pms.hooks";
import InputForm from "../../../components/Pms/InputForm/InputForm";
import { PmsReportSchema } from "../../../schema/pmsReport.schema";
import SelectForm from "../../../components/Pms/SelectForm/SelectForm";
import { EPrimarySubmissionMethod, EReportType } from "../../../utils/types";
import "./AddPMSReport.css";

const AddPMSReport = ({
  open,
  setOpen,
  refetchReports,
  editData,
  disable,
  isReportListVisible,
}) => {
  const { isLoading: isLoadingPMS, data: pmsData } = useGetPMS();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(PmsReportSchema),
    mode: "onChange",
  });

  const { mutate: addPMSReport, isLoading: addReportLoading } =
    useAddPMSReportList({
      onSuccess: () => {
        message.success("PMS Report Created Successfully");
        if (isReportListVisible || editData) refetchReports();
        setOpen(false);
        reset();
      },
      onError: (error) => {
        message.error(error.response?.data?.message ?? "Server Error");
      },
    });

  const { mutate: updatePMSReport } = useUpdatePMSReport({
    onSuccess: () => {
      message.success("PMS Report Updated Successfully");
      refetchReports();
      setOpen(false);
      reset();
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  const onSubmit = async (data) => {
    const { pms, ...rest } = data;
    const updatedSubmitObject = {
      pmsId: pms,
      ...rest,
    };
    if (editData) {
      await updatePMSReport({ ...updatedSubmitObject, _id: editData._id });
    } else await addPMSReport(updatedSubmitObject);
  };

  const submissionMethodFormItem = {
    name: "source_type",
    label: "Primary Submission Method",
    placeholder: "Primary Submission Method",
  };

  const reportTypeFormItem = {
    name: "mydocs_report_type",
    label: "myDocs Report Type",
    placeholder: "myDocs Report Type",
  };

  const submissionMethodDataSource = Object.keys(EPrimarySubmissionMethod).map(
    (key) => ({
      _id: EPrimarySubmissionMethod[key],
      primarySubmissionMethod: EPrimarySubmissionMethod[key],
    }),
  );

  const reportTypedDataSource = Object.keys(EReportType).map((key) => ({
    _id: EReportType[key],
    primarySubmissionMethod: EReportType[key],
  }));

  const onCancelHandler = () => {
    reset();
    setOpen(false);
  };

  useEffect(() => {
    if (editData) reset(editData);
  }, [editData]);

  return (
    <Modal
      className="addReportModal"
      title={editData ? `Edit PMS Report` : `Add PMS Report`}
      width={700}
      open={open}
      footer={null}
      onCancel={onCancelHandler}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <PMSSelect
          dataSource={pmsData}
          control={control}
          errors={errors}
          required
          loading={isLoadingPMS}
          disable={disable}
        />
        <InputForm
          control={control}
          errors={errors}
          name="mydocs_report_name"
          label="myDocs Report Name"
          placeHolder="myDocs Report Name"
          required
          layOutSpan="24"
          disable={disable}
        />
        <InputForm
          control={control}
          errors={errors}
          name="report_name"
          label="PMS Report Name"
          placeHolder="PMS Report Name"
          required
          layOutSpan="24"
          disable={disable}
        />
        <InputForm
          control={control}
          errors={errors}
          name="report_code"
          label="Report Code/Spooler Code"
          placeHolder="Report Code/Spooler Code"
          layOutSpan="24"
        />
        <SelectForm
          formItem={submissionMethodFormItem}
          dataSource={submissionMethodDataSource}
          control={control}
          errors={errors}
          loading={isLoadingPMS}
          layOutSpan="24"
          valueKey="primarySubmissionMethod"
          required
        />
        <SelectForm
          formItem={reportTypeFormItem}
          dataSource={reportTypedDataSource}
          control={control}
          errors={errors}
          loading={isLoadingPMS}
          layOutSpan="24"
          valueKey="reportType"
          required
        />

        <InputForm
          control={control}
          errors={errors}
          name="file_name"
          label="File name"
          placeHolder="File Name"
          layOutSpan="24"
        />
        <Row justify="space-between" gutter={12}>
          <Col span={12}>
            <Button block onClick={() => onCancelHandler()}>
              Cancel
            </Button>
          </Col>
          <Col span={12}>
            <Button
              block
              type="primary"
              htmlType="submit"
              loading={addReportLoading}
              disabled={!isValid}
            >
              Save
            </Button>
          </Col>
        </Row>
      </form>
    </Modal>
  );
};

export default AddPMSReport;
